import React, { useState } from 'react';
import { Dumbbell, Plus, Trash2 } from 'lucide-react';
import {
  Alert, Badge, Button, Card, Empty, Field, Input, Page, PageHead, Select, Stat, Tabs,
} from '../components/ui';
import WorkoutCoach from '../components/WorkoutCoach';
import { MUSCLE_GROUPS, n0, timeOf, titleise } from '../lib/format';

const BLANK = { name: '', muscle_group: 'chest', sets: '', reps: '', weight: '', duration: '' };

export default function Exercise({ exerciseLog, onAdd, onDelete }) {
  const [tab, setTab] = useState('log');
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true); setError('');
    try {
      await onAdd({
        name: form.name.trim(),
        muscle_group: form.muscle_group,
        sets: parseInt(form.sets, 10) || null,
        reps: parseInt(form.reps, 10) || null,
        weight: parseFloat(form.weight) || null,
        duration: parseInt(form.duration, 10) || null,
      });
      setForm({ ...BLANK, muscle_group: form.muscle_group });
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const volume = exerciseLog.reduce(
    (s, e) => s + (+e.sets || 0) * (+e.reps || 0) * (+e.weight || 0), 0
  );
  const sets = exerciseLog.reduce((s, e) => s + (+e.sets || 0), 0);
  const minutes = exerciseLog.reduce((s, e) => s + (+e.duration || 0), 0);

  return (
    <Page>
      <PageHead
        title="Exercise"
        actions={
          <Tabs
            value={tab}
            onChange={setTab}
            options={[
              { value: 'log', label: "Today's session" },
              { value: 'coach', label: 'Balance & suggestions' },
            ]}
          />
        }
      >
        {tab === 'log'
          ? "Today's training session."
          : 'Where your volume is going, and which muscle groups are being missed.'}
      </PageHead>

      {tab === 'coach' && <WorkoutCoach />}

      {tab === 'log' && (<>
      <div className="grid grid-4">
        <Stat label="Exercises" value={exerciseLog.length} />
        <Stat label="Working sets" value={sets} />
        <Stat label="Total volume" value={n0(volume)} unit="kg" note="sets × reps × load" />
        <Stat label="Cardio" value={minutes} unit="min" />
      </div>

      <Card title="Log an exercise">
        <form onSubmit={submit}>
          {error && <div style={{ marginBottom: 12 }}><Alert tone="crit">{error}</Alert></div>}
          <div className="grid" style={{ gridTemplateColumns: 'minmax(200px, 2.2fr) minmax(130px, 1fr)' }}>
            <Field label="Exercise">
              <Input placeholder="Bench press" value={form.name} onChange={set('name')} required />
            </Field>
            <Field label="Muscle group">
              <Select
                options={MUSCLE_GROUPS.map((m) => ({ value: m, label: titleise(m) }))}
                value={form.muscle_group}
                onChange={set('muscle_group')}
              />
            </Field>
          </div>
          <div className="grid grid-4" style={{ marginTop: 14 }}>
            <Field label="Sets"><Input numeric type="number" min="0" placeholder="4" value={form.sets} onChange={set('sets')} /></Field>
            <Field label="Reps"><Input numeric type="number" min="0" placeholder="8" value={form.reps} onChange={set('reps')} /></Field>
            <Field label="Load · kg"><Input numeric type="number" step="any" min="0" placeholder="80" value={form.weight} onChange={set('weight')} /></Field>
            <Field label="Duration · min" hint="For cardio"><Input numeric type="number" min="0" placeholder="30" value={form.duration} onChange={set('duration')} /></Field>
          </div>
          <Button variant="primary" icon={Plus} type="submit" disabled={busy || !form.name.trim()}
            loading={busy} style={{ marginTop: 16 }}>
            Add to session
          </Button>
        </form>
      </Card>

      <Card title={`Today (${exerciseLog.length})`} flush>
        {exerciseLog.length === 0 ? (
          <Empty icon={Dumbbell} title="No exercises logged">
            Log your lifts to track volume over time.
          </Empty>
        ) : (
          exerciseLog.map((e) => (
            <div className="item" key={e.id}>
              <Badge tone="muted">{titleise(e.muscle_group)}</Badge>
              <div className="grow">
                <div className="item-title">{e.name}</div>
                <div className="item-sub">{timeOf(e.logged_at)}</div>
              </div>
              <span className="num dim" style={{ fontSize: 12.5 }}>
                {e.sets && e.reps ? `${e.sets} × ${e.reps}` : ''}
                {e.weight ? `  ·  ${n0(e.weight)} kg` : ''}
                {e.duration ? `${e.duration} min` : ''}
              </span>
              <Button size="sm" variant="danger" icon={Trash2}
                onClick={() => onDelete(e.id)} aria-label={`Delete ${e.name}`} />
            </div>
          ))
        )}
      </Card>
      </>)}
    </Page>
  );
}
